import jobServices from 'ApiService/Placement/jobServices';
import Card from 'components/card';
import InputField from 'components/fields/InputField';
import ConfirmationModal from 'pages/Common/ConfirmationModal';
import Nodatafound from 'pages/Common/Nodatafound';
import SharedFilesPanel from 'pages/PlacementAdmin/AssignmentOverview/SharedFilesPanel';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { BallTriangle } from 'react-loader-spinner';

const ViewAssignedJobs = ({ studentId }) => {
  const [assignedJobs, setAssignedJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedJob, setExpandedJob] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Round editing state
  const [editingRound, setEditingRound] = useState(null);
  const [roundDetails, setRoundDetails] = useState({ roundNumber: '', status: '', feedback: '', date: '' });

  // Schedule interview popup
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [scheduleData, setScheduleData] = useState({ roundNumber: '', scheduledDate: '', scheduledTime: '', notes: '' });

  // Cancel interview
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [selectedInterviewId, setSelectedInterviewId] = useState(null);

  // Reschedule
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [rescheduleData, setRescheduleData] = useState({ interviewId: '', newDate: '', newTime: '', notes: '' });

  // Resend offer
  const [isResendModalOpen, setIsResendModalOpen] = useState(false);
  const [resendAssignmentId, setResendAssignmentId] = useState(null);

  // Reassign job
  const [isReassignModalOpen, setIsReassignModalOpen] = useState(false);
  const [reassignAssignmentId, setReassignAssignmentId] = useState(null);
  const [reassignExpiry, setReassignExpiry] = useState(24);
  const [reassignReason, setReassignReason] = useState('');

  const [currentAssignmentId, setCurrentAssignmentId] = useState(null);

  // Delete assignment
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteAssignmentId, setDeleteAssignmentId] = useState(null);

  // Edit assignment (change assigned job / expiry)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editAssignmentId, setEditAssignmentId] = useState(null);
  const [editJobId, setEditJobId] = useState('');
  const [editExpiry, setEditExpiry] = useState(24);
  const [jobPostOptions, setJobPostOptions] = useState([]);
  const [editCurrentJob, setEditCurrentJob] = useState(null);

  // Offer details modal
  const [isOfferDetailsModalOpen, setIsOfferDetailsModalOpen] = useState(false);
  const [showAcceptConfirmation, setShowAcceptConfirmation] = useState(false);
  const [errors, setErrors] = useState([]);
  const [formData, setFormData] = useState({
    joiningDate: '', salary: '', benefits: '', bondDuration: '', location: '',
  });
  const [offerDetails, setOfferDetails] = useState(null);
  const [offerLetterName, setOfferLetterName] = useState('');

  const fetchAssignedJobs = async () => {
    try {
      setLoading(true);
      const response = await jobServices.getAssignedJobs(studentId);
      if (response.data.success) {
        setAssignedJobs(response.data.assignedJobs);
      } else {
        toast.error('Failed to fetch assigned jobs.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to fetch assigned jobs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssignedJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  useEffect(() => {
    const newErrors = [];
    if (!formData.joiningDate.trim() || !formData.salary || !formData.benefits.trim() || !formData.bondDuration || !formData.location.trim() || !formData.offerLetterFile) {
      newErrors.push('All fields are mandatory.');
    }
    setErrors(newErrors);
  }, [formData]);

  const toggleJobExpansion = (jobId) => {
    setExpandedJob(expandedJob === jobId ? null : jobId);
    setEditingRound(null);
    setRoundDetails({ roundNumber: '', status: '', feedback: '', date: '' });
  };

  const handleRoundChange = (e) => {
    const { name, value } = e.target;
    setRoundDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddOrUpdateRound = async () => {
    const missingFields = [];
    if (!roundDetails.roundNumber) missingFields.push('Round Number');
    if (!roundDetails.status) missingFields.push('Status');
    if (missingFields.length > 0) {
      toast.error(`Please fill in: ${missingFields.join(', ')}`);
      return;
    }
    try {
      setIsSubmitting(true);
      const roundToUpdate = [{ ...roundDetails, date: roundDetails.date || new Date() }];
      const response = await jobServices.updateRoundDetails(currentAssignmentId, roundToUpdate);
      if (response.data.success) {
        toast.success(editingRound ? 'Round updated!' : 'Round updated!');
        fetchAssignedJobs();
        setEditingRound(null);
        setRoundDetails({ roundNumber: '', status: '', feedback: '', date: '' });
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update round.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Schedule Interview
  const openScheduleModal = (assignmentId, defaultRound) => {
    setCurrentAssignmentId(assignmentId);
    setScheduleData({ roundNumber: defaultRound?.toString() || '', scheduledDate: '', scheduledTime: '', notes: '' });
    setIsScheduleModalOpen(true);
  };

  const handleScheduleConfirm = async () => {
    if (!scheduleData.roundNumber || !scheduleData.scheduledDate) {
      toast.error('Please fill Round Number and Date');
      return;
    }
    try {
      setIsSubmitting(true);
      const response = await jobServices.scheduleInterview(currentAssignmentId, {
        roundNumber: Number(scheduleData.roundNumber),
        scheduledDate: scheduleData.scheduledDate,
        scheduledTime: scheduleData.scheduledTime,
        notes: scheduleData.notes,
      });
      if (response.data.success) {
        toast.success('Interview scheduled!');
        setIsScheduleModalOpen(false);
        fetchAssignedJobs();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to schedule interview');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cancel Interview
  const openCancelModal = (assignmentId, interviewId) => {
    setCurrentAssignmentId(assignmentId);
    setSelectedInterviewId(interviewId);
    setIsCancelModalOpen(true);
  };

  const handleCancelConfirm = async () => {
    try {
      setIsSubmitting(true);
      const response = await jobServices.cancelInterview(currentAssignmentId, selectedInterviewId);
      if (response.data.success) {
        toast.success('Interview cancelled');
        setIsCancelModalOpen(false);
        fetchAssignedJobs();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to cancel interview');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reschedule Interview
  const openRescheduleModal = (assignmentId, interviewId) => {
    setCurrentAssignmentId(assignmentId);
    setRescheduleData({ interviewId, newDate: '', newTime: '', notes: '' });
    setIsRescheduleModalOpen(true);
  };

  const handleRescheduleConfirm = async () => {
    if (!rescheduleData.newDate) {
      toast.error('Please fill the new date');
      return;
    }
    try {
      setIsSubmitting(true);
      const response = await jobServices.rescheduleInterview(currentAssignmentId, {
        interviewId: rescheduleData.interviewId,
        newDate: rescheduleData.newDate,
        newTime: rescheduleData.newTime,
        notes: rescheduleData.notes,
      });
      if (response.data.success) {
        toast.success('Interview rescheduled');
        setIsRescheduleModalOpen(false);
        fetchAssignedJobs();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to reschedule interview');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Resend Offer
  const openResendModal = (assignmentId) => {
    setResendAssignmentId(assignmentId);
    setIsResendModalOpen(true);
  };

  const handleResendConfirm = async () => {
    try {
      setIsSubmitting(true);
      const response = await jobServices.resendOffer(resendAssignmentId);
      if (response.data.success) {
        toast.success('Offer resent to student');
        setIsResendModalOpen(false);
        fetchAssignedJobs();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to resend offer');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reassign Job
  const openReassignModal = (assignmentId) => {
    setReassignAssignmentId(assignmentId);
    setReassignExpiry(24);
    setReassignReason('');
    setIsReassignModalOpen(true);
  };

  const handleReassignConfirm = async () => {
    try {
      setIsSubmitting(true);
      const response = await jobServices.reassignJob(
        reassignAssignmentId,
        reassignExpiry,
        reassignReason
      );
      if (response.data.success) {
        toast.success('Job reassigned to student');
        setIsReassignModalOpen(false);
        fetchAssignedJobs();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to reassign job');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete Job Assignment
  const openDeleteModal = (assignmentId) => {
    setDeleteAssignmentId(assignmentId);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setIsSubmitting(true);
      const response = await jobServices.deleteJobAssignment(deleteAssignmentId);
      if (response.data.success) {
        toast.success('Job assignment deleted');
        setIsDeleteModalOpen(false);
        fetchAssignedJobs();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to delete job assignment');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Edit Job Assignment
  const openEditModal = async (job) => {
    setEditAssignmentId(job._id);
    setEditJobId(job.jobId?._id || '');
    setEditExpiry(job.assignmentExpiry || 24);
    setEditCurrentJob(job.jobId || null);
    setIsEditModalOpen(true);
    if (jobPostOptions.length === 0) {
      try {
        const response = await jobServices.getAllJobPost();
        if (response.data.success) {
          setJobPostOptions(response.data.jobPosts || []);
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to load job posts');
      }
    }
  };

  const handleEditConfirm = async () => {
    if (!editJobId) {
      toast.error('Please select a job');
      return;
    }
    try {
      setIsSubmitting(true);
      const response = await jobServices.editJobAssignment(editAssignmentId, editJobId, editExpiry);
      if (response.data.success) {
        toast.success('Job assignment updated');
        setIsEditModalOpen(false);
        fetchAssignedJobs();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update job assignment');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Offer details handlers (unchanged)
  const handleOfferFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAcceptOffer = (assignmentId) => {
    setCurrentAssignmentId(assignmentId);
    setIsOfferDetailsModalOpen(true);
  };

  const handleOfferFormSubmit = (e) => {
    e.preventDefault();
    if (errors.length > 0) { toast.error('Please fill all required fields.'); return; }
    setOfferDetails({
      joiningDate: formData.joiningDate,
      salary: Number(formData.salary),
      benefits: formData.benefits.split(',').map((b) => b.trim()),
      bondDuration: Number(formData.bondDuration),
      location: formData.location,
      offerLetterFile: formData.offerLetterFile,
      offerLetterName,
    });
    setIsOfferDetailsModalOpen(false);
    setShowAcceptConfirmation(true);
  };

  const handleSubmitOfferDetails = async () => {
    if (isSubmitting || !offerDetails) return;
    try {
      setIsSubmitting(true);
      const fd = new FormData();
      fd.append('joiningDate', offerDetails.joiningDate);
      fd.append('salary', offerDetails.salary);
      fd.append('benefits', JSON.stringify(offerDetails.benefits));
      fd.append('bondDuration', offerDetails.bondDuration);
      fd.append('location', offerDetails.location);
      fd.append('offerLetter', formData.offerLetterFile);
      fd.append('assignmentId', currentAssignmentId);
      const response = await jobServices.updateOfferDetails(fd);
      if (response.data.success) {
        toast.success('Offer details added');
        setShowAcceptConfirmation(false);
        fetchAssignedJobs();
        resetOfferForm();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to add offer details');
    } finally { setIsSubmitting(false); }
  };

  const handleOfferLetterChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type === 'application/pdf') {
        setOfferLetterName(file.name);
        setFormData((prev) => ({ ...prev, offerLetterFile: file }));
      } else {
        setErrors([...errors, 'Only PDF allowed.']);
      }
    }
  };

  const resetOfferForm = () => {
    setFormData({ joiningDate: '', salary: '', benefits: '', bondDuration: '', location: '', offerLetterFile: null });
    setOfferLetterName('');
    setErrors([]);
  };

  const closeAcceptConfirmation = () => {
    if (isSubmitting) return;
    setShowAcceptConfirmation(false);
    setIsOfferDetailsModalOpen(true);
  };

  const getStudentResponseBadge = (job) => {
    if (job.studentJobResponse === 'Will Attend') {
      return <span className="rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800">Student Will Attend — Ready for Interview</span>;
    }
    if (job.studentJobResponse === 'Cannot Attend') {
      return <span className="rounded-full bg-red-100 px-3 py-1 text-sm font-medium text-red-800">Student Cannot Attend</span>;
    }
    return <span className="rounded-full bg-yellow-100 px-3 py-1 text-sm font-medium text-yellow-800">Awaiting Student Response</span>;
  };

  const getCollapsedCardStatus = (job) => {
    const { studentJobResponse, overallStatus, studentRevert, viewedAt, scheduledInterviews } = job;
    const hasInterviews = scheduledInterviews?.length > 0;

    if (studentJobResponse === 'Cannot Attend')
      return { label: 'Cannot Attend', bg: 'bg-red-100 text-red-800', desc: '🔴 Student Declined Participation', descBg: 'bg-red-50 text-red-700' };
    if (overallStatus === 'Not Selected' || overallStatus === 'Rejected')
      return { label: overallStatus, bg: 'bg-red-100 text-red-800', desc: '🔴 Student Not Selected', descBg: 'bg-red-50 text-red-700' };
    if (overallStatus === 'Selected' && studentRevert === 'Accepted')
      return { label: 'Accepted', bg: 'bg-green-100 text-green-800', desc: '✅ Offer Accepted by Student', descBg: 'bg-green-50 text-green-700' };
    if (overallStatus === 'Selected' && (studentRevert === 'Declined' || studentRevert === 'Auto Declined'))
      return { label: 'Declined', bg: 'bg-red-100 text-red-800', desc: '❌ Offer Declined by Student', descBg: 'bg-red-50 text-red-700' };
    if (overallStatus === 'Selected' && studentRevert === 'Pending')
      return { label: 'Offer Sent', bg: 'bg-purple-100 text-purple-800', desc: '📄 Offer Sent — Awaiting Student Response', descBg: 'bg-purple-50 text-purple-700' };
    if (studentJobResponse === 'Will Attend' && hasInterviews)
      return { label: 'In Interview', bg: 'bg-green-100 text-green-800', desc: `🟢 Interview Process — ${scheduledInterviews.length} Round${scheduledInterviews.length > 1 ? 's' : ''}`, descBg: 'bg-green-50 text-green-700' };
    if (studentJobResponse === 'Will Attend' && !hasInterviews)
      return { label: 'Will Attend', bg: 'bg-green-100 text-green-800', desc: '🟢 Will Attend — Awaiting Interview Schedule', descBg: 'bg-green-50 text-green-700' };
    if (!viewedAt && studentJobResponse === 'Pending')
      return { label: 'New', bg: 'bg-blue-100 text-blue-800', desc: '👁 Not Viewed by Student Yet', descBg: 'bg-blue-50 text-blue-700' };
    if (viewedAt && studentJobResponse === 'Pending')
      return { label: 'Awaiting', bg: 'bg-yellow-100 text-yellow-800', desc: '⏳ Viewed — Awaiting Student Response', descBg: 'bg-yellow-50 text-yellow-700' };
    return { label: overallStatus || 'Assigned', bg: 'bg-gray-100 text-gray-800', desc: `Status: ${overallStatus || 'Assigned'}`, descBg: 'bg-gray-50 text-gray-700' };
  };

  const renderScheduleSection = (job) => {
    if (job.studentJobResponse !== 'Will Attend') return null;
    const hasScheduled = job.scheduledInterviews?.length > 0;

    return (
      <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-700 dark:bg-navy-800">
        <h4 className="mb-2 font-semibold text-blue-700 dark:text-blue-300">Interview Schedule</h4>
        {hasScheduled ? (
          <div className="space-y-2">
            {job.scheduledInterviews.map((interview, idx) => {
              const statusColor = interview.status === 'Cancelled' ? 'text-red-600' : interview.status === 'Rescheduled' ? 'text-yellow-600' : 'text-green-600';
              return (
                  <div key={idx} className="flex items-center justify-between rounded bg-white p-2 text-sm dark:bg-navy-700">
                  <div>
                    <strong>Round {interview.roundNumber}:</strong>{' '}
                    <span className={statusColor}>
                      {new Date(interview.scheduledDate).toLocaleDateString()}{interview.scheduledTime ? ` at ${interview.scheduledTime}` : ''}
                    </span>
                    {interview.notes && <p className="mt-1 text-xs text-gray-500">📌 {interview.notes}</p>}
                    {interview.status === 'Cancelled' && <span className="ml-2 text-red-600">[Cancelled]</span>}
                    {interview.status === 'Rescheduled' && <span className="ml-2 text-yellow-600">[Rescheduled]</span>}
                  </div>
                  {interview.status !== 'Cancelled' && (
                    <div className="flex gap-2">
                      <button onClick={() => openCancelModal(job._id, interview._id)} className="text-xs text-red-500 hover:text-red-700" disabled={isSubmitting}>Cancel</button>
                      <button onClick={() => openRescheduleModal(job._id, interview._id)} className="text-xs text-blue-500 hover:text-blue-700" disabled={isSubmitting}>Reschedule</button>
                    </div>
                  )}
                  {interview.status === 'Cancelled' && (
                    <button onClick={() => openScheduleModal(job._id, interview.roundNumber)} className="text-xs text-blue-500 hover:text-blue-700" disabled={isSubmitting}>Schedule Again</button>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="mb-2 text-sm text-gray-600">No interviews scheduled yet.</p>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); openScheduleModal(job._id, (job.rounds?.length || 0) + 1); }}
          className="mt-2 rounded bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
          disabled={isSubmitting}
        >
          Schedule Next Round
        </button>
      </div>
    );
  };

  const renderRoundsSection = (job) => {
    if (!job.rounds?.length) return null;
    return (
      <div className="mb-4">
        <h4 className="mb-2 font-semibold">Interview Rounds</h4>
        {job.rounds.map((round, index) => {
          const isEditing = editingRound === round.roundNumber;
          return (
            <div key={index} className="mb-2 rounded-lg border border-gray-200 bg-white p-3 dark:border-white/10 dark:bg-navy-700">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">
                  Round {round.roundNumber} —
                  {isEditing ? (
                   <select name="status" value={roundDetails.status} onChange={handleRoundChange} className="ml-2 rounded border px-2 py-1 text-sm dark:bg-navy-800 dark:text-white">
                       <option value="">Select</option>
                       <option value="Pending">Pending</option>
                       <option value="Shortlisted">Shortlisted</option>
                       <option value="Not Shortlisted">Not Shortlisted</option>
                       <option value="Attended">Attended</option>
                       <option value="Selected for Next Round">Selected for Next Round</option>
                       <option value="Selected">Selected</option>
                       <option value="Rejected">Rejected</option>
                       <option value="Not Attended">Not Attended</option>
                      <option value="On Hold">On Hold</option>
                    </select>
                  ) : (
                                 <span className={`ml-2 rounded-full px-2 py-0.5 text-xs font-medium ${
                       round.status === 'Selected' || round.status === 'Selected for Next Round' || round.status === 'Shortlisted' ? 'bg-green-100 text-green-800' :
                       round.status === 'Interview Scheduled' ? 'bg-blue-100 text-blue-800' :
                       round.status === 'Rejected' || round.status === 'Not Selected' || round.status === 'Not Shortlisted' ? 'bg-red-100 text-red-800' :
                       round.status === 'Not Attended' ? 'bg-orange-100 text-orange-800' :
                       round.status === 'On Hold' ? 'bg-yellow-100 text-yellow-800' :
                       'bg-gray-100 text-gray-800'
                     }`}>{round.status}</span>
                  )}
                </p>
                <div className="flex gap-2">
                  {isEditing ? (
                    <>
                      <button onClick={() => { handleAddOrUpdateRound(); }} disabled={isSubmitting} className="text-xs text-green-600 hover:text-green-800">Save</button>
                      <button onClick={() => { setEditingRound(null); setRoundDetails({ roundNumber: '', status: '', feedback: '', date: '' }); }} className="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                    </>
                  ) : (
                    <>
                      <button onClick={(e) => { e.stopPropagation(); setCurrentAssignmentId(job._id); setEditingRound(round.roundNumber); setRoundDetails({ roundNumber: round.roundNumber.toString(), status: round.status, feedback: round.feedback || '', date: round.date ? new Date(round.date).toISOString().split('T')[0] : '' }); }} className="text-xs text-blue-500 hover:text-blue-700">Edit</button>
                    </>
                  )}
                </div>
              </div>
              {round.roundNumber === 1 && !isEditing && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {(() => {
                    const isShortlisted = round.status === 'Shortlisted';
                    const isNotShortlisted = round.status === 'Not Shortlisted';
                    return (
                      <>
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              setIsSubmitting(true);
                              const response = await jobServices.updateRoundDetails(job._id, [{
                                roundNumber: round.roundNumber,
                                status: 'Shortlisted',
                                feedback: round.feedback || '',
                                date: round.date || new Date(),
                              }]);
                              if (response.data.success) {
                                toast.success('Round marked as Shortlisted');
                                fetchAssignedJobs();
                              }
                            } catch (error) {
                              toast.error(error?.response?.data?.message || 'Failed to update round');
                            } finally {
                              setIsSubmitting(false);
                            }
                          }}
                          disabled={isSubmitting}
                          className={`rounded-full px-3 py-1 text-[11px] font-medium disabled:opacity-50 ${isShortlisted ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600'}`}
                        >
                          Shortlisted
                        </button>
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              setIsSubmitting(true);
                              const response = await jobServices.updateRoundDetails(job._id, [{
                                roundNumber: round.roundNumber,
                                status: 'Not Shortlisted',
                                feedback: round.feedback || '',
                                date: round.date || new Date(),
                              }]);
                              if (response.data.success) {
                                toast.success('Round marked as Not Shortlisted');
                                fetchAssignedJobs();
                              }
                            } catch (error) {
                              toast.error(error?.response?.data?.message || 'Failed to update round');
                            } finally {
                              setIsSubmitting(false);
                            }
                          }}
                          disabled={isSubmitting}
                          className={`rounded-full px-3 py-1 text-[11px] font-medium disabled:opacity-50 ${isNotShortlisted ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600'}`}
                        >
                          Not Shortlisted
                        </button>
                        {round.status === 'Shortlisted' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); openScheduleModal(job._id, round.roundNumber); }}
                            disabled={isSubmitting}
                            className="rounded-full bg-blue-600 px-3 py-1 text-[11px] font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                          >
                            Schedule Interview
                          </button>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
              {round.feedback && !isEditing && <p className="mt-1 text-xs text-gray-600">Feedback: {round.feedback}</p>}
              {isEditing && (
                <div className="mt-2">
                  <textarea name="feedback" value={roundDetails.feedback} onChange={handleRoundChange} placeholder="Feedback" className="w-full rounded border p-2 text-sm dark:bg-navy-800 dark:text-white" rows="2" />
                  <InputField label="Date" type="date" name="date" value={roundDetails.date} onChange={handleRoundChange} className="mt-2 w-full rounded border p-2" />
                </div>
              )}
              {round.studentStatus && (
                <p className={`mt-1 text-xs font-medium ${round.studentStatus === 'Going To Attend' ? 'text-green-600' : round.studentStatus === 'Not Going To Attend' ? 'text-red-600' : 'text-gray-500'}`}>
                  Student Response: {round.studentStatus}
                </p>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderReassignSection = (job) => {
    const isMissed =
      job.studentJobResponse === 'Cannot Attend' ||
      job.studentRevert === 'Auto Declined';
    if (!isMissed) return null;
    const expiryLabel =
      job.assignmentExpiry || job.assignmentExpiry === 0
        ? `${job.assignmentExpiry} hours`
        : '24 hours';
    return (
      <div className="mb-4 rounded-lg border border-orange-200 bg-orange-50 p-4 dark:border-orange-700 dark:bg-navy-800">
        <h4 className="mb-1 font-semibold text-orange-700 dark:text-orange-300">
          Reassign Job
        </h4>
        <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
          {job.revertReason
            ? `The student missed this assignment: ${job.revertReason}.`
            : 'The student missed this assignment.'}
          {' '}Reassigning will restart the response timer so the student can apply again.
          {job.reassignCount > 0 && (
            <span className="ml-1 text-xs">(Reassigned {job.reassignCount} time{job.reassignCount > 1 ? 's' : ''})</span>
          )}
        </p>
        <button
          onClick={(e) => { e.stopPropagation(); openReassignModal(job._id); }}
          disabled={isSubmitting}
          className="rounded bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-50"
        >
          Reassign Job
        </button>
        <span className="ml-2 text-xs text-gray-500">Previous expiry: {expiryLabel}</span>
      </div>
    );
  };

  const renderOfferSection = (job) => {
    if (job.overallStatus !== 'Selected') return null;
    return (
      <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-700 dark:bg-navy-800">
        <h4 className="mb-2 font-semibold text-green-700 dark:text-green-300">Offer Section</h4>
        <p className="mb-2 text-sm">
          <strong>Student Response:</strong>{' '}
          <span className={job.studentRevert === 'Accepted' ? 'text-green-600' : job.studentRevert === 'Declined' || job.studentRevert === 'Auto Declined' ? 'text-red-600' : 'text-yellow-600'}>
            {job.studentRevert || 'Pending'}
          </span>
          {job.revertReason && <span className="ml-2 text-xs text-gray-500">— {job.revertReason}</span>}
        </p>
        {job.studentRevert === 'Accepted' && !job.isOfferDetailsAdded && (
          <button onClick={(e) => { e.stopPropagation(); handleAcceptOffer(job._id); }} className="rounded bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700">
            Add Offer Details
          </button>
        )}
        {job.isOfferDetailsAdded && job.offerDetails && (
          <div className="mt-2 rounded bg-white p-3 text-sm dark:bg-navy-700">
            <p><strong>Joining:</strong> {new Date(job.offerDetails.joiningDate).toLocaleDateString()}</p>
            <p><strong>Salary:</strong> ₹{job.offerDetails.salary?.toLocaleString()}</p>
            <p><strong>Location:</strong> {job.offerDetails.location}</p>
          </div>
        )}
        {(job.studentRevert === 'Declined' && !job.offerResendAllowed) && (
          <button onClick={(e) => { e.stopPropagation(); openResendModal(job._id); }} className="mt-2 rounded bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600" disabled={isSubmitting}>
            Resend Offer
          </button>
        )}
        {job.offerResendAllowed && <p className="mt-1 text-xs text-green-600">Offer has been resent to student.</p>}
      </div>
    );
  };

  const renderJobCard = (job) => {
    const isExpanded = expandedJob === job._id;
    const status = getCollapsedCardStatus(job);

    return (
      <div key={job._id} className="mb-4 overflow-hidden rounded-lg bg-white shadow dark:bg-navy-700">
        <div className="flex cursor-pointer items-start gap-4 p-5 hover:bg-gray-50 dark:hover:bg-navy-900" onClick={() => toggleJobExpansion(job._id)}>
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-100 text-xl dark:bg-navy-600">💼</span>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h3 className="text-base font-semibold dark:text-white">{job.jobId?.jobTitle || 'Company Assignment'}</h3>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-gray-500 dark:text-gray-400">
                  <span>{job.jobId?.industry || 'Company'}</span>
                  {job.jobId?.location && <><span className="text-gray-300">|</span><span>📍 {job.jobId.location}</span></>}
                  {job.jobId?.type && <><span className="text-gray-300">|</span><span>⏰ {job.jobId.type}</span></>}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span className={`rounded-full px-3 py-1 text-xs font-medium ${status.bg}`}>{status.label}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); openEditModal(job); }}
                  disabled={isSubmitting}
                  className="rounded-full border border-blue-300 px-3 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 disabled:opacity-50 dark:border-blue-700 dark:text-blue-400 dark:hover:bg-navy-800"
                >
                  Edit
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); openDeleteModal(job._id); }}
                  disabled={isSubmitting}
                  className="rounded-full border border-red-300 px-3 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-navy-800"
                >
                  Delete
                </button>
              </div>
            </div>
            {job.jobId?.requiredSkills?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {job.jobId.requiredSkills.slice(0, 6).map((skill, i) => (
                  <span key={i} className="rounded-md bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:bg-navy-600 dark:text-gray-300">{skill}</span>
                ))}
                {job.jobId.requiredSkills.length > 6 && (
                  <span className="text-[11px] text-gray-400">+{job.jobId.requiredSkills.length - 6} more</span>
                )}
              </div>
            )}
            {job.jobId?.responsibilities?.length > 0 && (
              <p className="mt-2 truncate text-xs text-gray-400">
                📝 {job.jobId.responsibilities.slice(0, 2).join(' · ')}
                {job.jobId.responsibilities.length > 2 ? ' ...' : ''}
              </p>
            )}
            <div className={`mt-3 rounded-md px-3 py-2 text-sm font-medium ${status.descBg}`}>
              {status.desc}
              {job.revertReason && <div className="mt-1 text-xs font-normal opacity-80">Reason: {job.revertReason}</div>}
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-400">
              <span>👤 {job.assignedBy?.name || 'Admin'}</span>
              <span>📅 {new Date(job.assignedDate).toLocaleDateString()}</span>
              {job.notes && <span className="truncate">📌 {job.notes}</span>}
            </div>
          </div>
        </div>
        {isExpanded && (
          <div className="border-t border-gray-100 bg-gray-50 p-5 dark:border-white/10 dark:bg-navy-700">
            {renderScheduleSection(job)}
            {renderRoundsSection(job)}
            {renderOfferSection(job)}
            {renderReassignSection(job)}
            <div className="mt-4">
              <SharedFilesPanel assignmentId={job._id} />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Card className="mt-4 w-full rounded-[20px] p-6 md:mt-5">
      <h1 className="mb-4 text-2xl font-bold">Assigned Jobs</h1>
      {loading ? (
        <div className="flex h-40 items-center justify-center"><BallTriangle color="#5431ed" /></div>
      ) : assignedJobs.length === 0 ? (
        <div className="flex h-40 items-center justify-center text-gray-600"><Nodatafound title="No job listings found" /></div>
      ) : (
        <div className="space-y-4">{assignedJobs.map(renderJobCard)}</div>
      )}

      {/* Schedule Interview Modal (custom) */}
      {isScheduleModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-lg dark:bg-navy-700">
            <h2 className="mb-4 text-lg font-semibold dark:text-white">Schedule Interview</h2>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium dark:text-white">Round Number</label>
                <input type="number" value={scheduleData.roundNumber} onChange={(e) => setScheduleData(p => ({ ...p, roundNumber: e.target.value }))} className="w-full rounded border p-2 text-sm dark:bg-navy-800 dark:text-white" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium dark:text-white">Date</label>
                <input type="date" value={scheduleData.scheduledDate} onChange={(e) => setScheduleData(p => ({ ...p, scheduledDate: e.target.value }))} className="w-full rounded border p-2 text-sm dark:bg-navy-800 dark:text-white" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium dark:text-white">Time (optional)</label>
                <input type="time" value={scheduleData.scheduledTime} onChange={(e) => setScheduleData(p => ({ ...p, scheduledTime: e.target.value }))} className="w-full rounded border p-2 text-sm dark:bg-navy-800 dark:text-white" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium dark:text-white">Notes (optional)</label>
                <textarea value={scheduleData.notes} onChange={(e) => setScheduleData(p => ({ ...p, notes: e.target.value }))} rows={2} className="w-full rounded border p-2 text-sm dark:bg-navy-800 dark:text-white" placeholder="Any additional notes..." />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setIsScheduleModalOpen(false)} disabled={isSubmitting} className="rounded-full border-2 border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100">Cancel</button>
              <button onClick={handleScheduleConfirm} disabled={isSubmitting} className="rounded-full bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600 disabled:opacity-50">{isSubmitting ? 'Scheduling...' : 'Schedule'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Interview Modal */}
      <ConfirmationModal isOpen={isCancelModalOpen} onClose={() => !isSubmitting && setIsCancelModalOpen(false)} onConfirm={handleCancelConfirm}
        title="Cancel Interview" message="Are you sure you want to cancel this interview? The student will be notified."
        confirmText={isSubmitting ? 'Cancelling...' : 'Yes, Cancel'} cancelText="Go Back" icon="warning" disableConfirm={isSubmitting} />

      {/* Reschedule Interview Modal (custom) */}
      {isRescheduleModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-lg dark:bg-navy-700">
            <h2 className="mb-4 text-lg font-semibold dark:text-white">Reschedule Interview</h2>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium dark:text-white">New Date</label>
                <input type="date" value={rescheduleData.newDate} onChange={(e) => setRescheduleData(p => ({ ...p, newDate: e.target.value }))} className="w-full rounded border p-2 text-sm dark:bg-navy-800 dark:text-white" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium dark:text-white">New Time (optional)</label>
                <input type="time" value={rescheduleData.newTime} onChange={(e) => setRescheduleData(p => ({ ...p, newTime: e.target.value }))} className="w-full rounded border p-2 text-sm dark:bg-navy-800 dark:text-white" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium dark:text-white">Notes (optional)</label>
                <textarea value={rescheduleData.notes} onChange={(e) => setRescheduleData(p => ({ ...p, notes: e.target.value }))} rows={2} className="w-full rounded border p-2 text-sm dark:bg-navy-800 dark:text-white" placeholder="Any additional notes..." />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setIsRescheduleModalOpen(false)} disabled={isSubmitting} className="rounded-full border-2 border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100">Cancel</button>
              <button onClick={handleRescheduleConfirm} disabled={isSubmitting} className="rounded-full bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600 disabled:opacity-50">{isSubmitting ? 'Rescheduling...' : 'Reschedule'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Resend Offer Modal */}
      <ConfirmationModal isOpen={isResendModalOpen} onClose={() => !isSubmitting && setIsResendModalOpen(false)} onConfirm={handleResendConfirm}
        title="Resend Offer" message="This will reset the student's offer status to pending and restart the 24-hour timer. Proceed?"
        confirmText={isSubmitting ? 'Resending...' : 'Yes, Resend Offer'} cancelText="Cancel" icon="info" disableConfirm={isSubmitting} />

      {/* Reassign Job Modal */}
      {isReassignModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-lg dark:bg-navy-700">
            <h2 className="mb-4 text-lg font-semibold dark:text-white">Reassign Job</h2>
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
              This will reset the student's response status to pending and restart the response timer. The student will be notified again.
            </p>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium dark:text-white">New Response Deadline</label>
                <select value={reassignExpiry} onChange={(e) => setReassignExpiry(Number(e.target.value))} className="w-full rounded border p-2 text-sm dark:bg-navy-800 dark:text-white">
                  <option value={24}>24 Hours (Default)</option>
                  <option value={12}>12 Hours</option>
                  <option value={6}>6 Hours</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium dark:text-white">Reason (optional)</label>
                <textarea value={reassignReason} onChange={(e) => setReassignReason(e.target.value)} rows={2} className="w-full rounded border p-2 text-sm dark:bg-navy-800 dark:text-white" placeholder="e.g., Student could not apply within the deadline" />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setIsReassignModalOpen(false)} disabled={isSubmitting} className="rounded-full border-2 border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100">Cancel</button>
              <button onClick={handleReassignConfirm} disabled={isSubmitting} className="rounded-full bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-50">{isSubmitting ? 'Reassigning...' : 'Reassign Job'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Job Assignment Modal */}
      <ConfirmationModal isOpen={isDeleteModalOpen} onClose={() => !isSubmitting && setIsDeleteModalOpen(false)} onConfirm={handleDeleteConfirm}
        title="Delete Assigned Job" message="This will permanently remove this job assignment from the student's account. This cannot be undone. Proceed?"
        confirmText={isSubmitting ? 'Deleting...' : 'Yes, Delete'} cancelText="Cancel" icon="warning" disableConfirm={isSubmitting} />

      {/* Edit Job Assignment Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-lg dark:bg-navy-700">
            <h2 className="mb-4 text-lg font-semibold dark:text-white">Edit Job Assignment</h2>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium dark:text-white">Assigned Job</label>
                <select
                  value={editJobId}
                  onChange={(e) => setEditJobId(e.target.value)}
                  className="mt-2 w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
                >
                  <option value="">Select a job</option>
                  {editCurrentJob && !jobPostOptions.some((j) => j._id === editCurrentJob._id) && (
                    <option value={editCurrentJob._id}>{editCurrentJob.jobTitle} (current)</option>
                  )}
                  {jobPostOptions.map((job) => (
                    <option key={job._id} value={job._id}>{job.jobTitle}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium dark:text-white">Response Deadline</label>
                <select value={editExpiry} onChange={(e) => setEditExpiry(Number(e.target.value))} className="w-full rounded border p-2 text-sm dark:bg-navy-800 dark:text-white">
                  <option value={24}>24 Hours</option>
                  <option value={12}>12 Hours</option>
                  <option value={6}>6 Hours</option>
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setIsEditModalOpen(false)} disabled={isSubmitting} className="rounded-full border-2 border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100">Cancel</button>
              <button onClick={handleEditConfirm} disabled={isSubmitting || !editJobId} className="rounded-full bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600 disabled:opacity-50">{isSubmitting ? 'Saving...' : 'Save Changes'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Offer Details Modal */}
      {isOfferDetailsModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl dark:bg-navy-700">
            <h2 className="mb-4 text-xl font-semibold dark:text-white">Enter Job Offer Details</h2>
            <form onSubmit={handleOfferFormSubmit}>
              <InputField label="Joining Date" type="date" name="joiningDate" value={formData.joiningDate} onChange={handleOfferFormChange} className="mb-3 w-full rounded border p-2" disabled={isSubmitting} />
              <InputField label="Annual Salary (₹)" type="number" name="salary" value={formData.salary} onChange={handleOfferFormChange} className="mb-3 w-full rounded border p-2" placeholder="Enter annual salary" disabled={isSubmitting} />
              <InputField label="Benefits (comma-separated)" type="text" name="benefits" value={formData.benefits} onChange={handleOfferFormChange} className="mb-3 w-full rounded border p-2" placeholder="e.g., Health insurance, PF" disabled={isSubmitting} />
              <InputField label="Bond Duration (months)" type="number" name="bondDuration" value={formData.bondDuration} onChange={handleOfferFormChange} className="mb-3 w-full rounded border p-2" placeholder="Enter bond duration" disabled={isSubmitting} />
              <InputField label="Job Location" type="text" name="location" value={formData.location} onChange={handleOfferFormChange} className="mb-3 w-full rounded border p-2" placeholder="Enter job location" disabled={isSubmitting} />
              <div className="mb-4">
                <label className="mb-1 block text-sm font-medium dark:text-white">Upload Offer Letter</label>
                <label className="flex h-12 w-full cursor-pointer items-center justify-center rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-600 hover:bg-gray-100 dark:border-white/10 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-800">
                  <input type="file" accept="application/pdf" className="hidden" onChange={handleOfferLetterChange} disabled={isSubmitting} />
                  {offerLetterName ? offerLetterName : 'Choose Offer Letter PDF'}
                </label>
              </div>
              {errors.length > 0 && <div className="mb-4 text-sm text-red-600">{errors.map((err, idx) => <p key={idx}>{err}</p>)}</div>}
              <div className="flex justify-end gap-4">
                <button type="button" onClick={() => { setIsOfferDetailsModalOpen(false); resetOfferForm(); }} className="rounded bg-gray-300 px-4 py-2 text-sm hover:bg-gray-400" disabled={isSubmitting}>Cancel</button>
                <button type="submit" className={`rounded px-4 py-2 text-sm text-white ${errors.length > 0 || isSubmitting ? 'cursor-not-allowed bg-indigo-300' : 'bg-indigo-600 hover:bg-indigo-700'}`} disabled={errors.length > 0 || isSubmitting}>{isSubmitting ? 'Submitting...' : 'Continue'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Accept Offer Confirmation */}
      <ConfirmationModal isOpen={showAcceptConfirmation} onClose={closeAcceptConfirmation} onConfirm={handleSubmitOfferDetails}
        title="Confirm Offer Details"
        message={`Are you sure?\nJoining: ${offerDetails?.joiningDate || ''}\nSalary: ₹${offerDetails?.salary?.toLocaleString() || ''}\nBenefits: ${offerDetails?.benefits?.join(', ') || ''}\nBond: ${offerDetails?.bondDuration || ''} months\nLocation: ${offerDetails?.location || ''}`}
        confirmText={isSubmitting ? 'Processing...' : 'Yes, Add Details'} cancelText="Go Back" icon="info" disableConfirm={isSubmitting} />
    </Card>
  );
};

export default ViewAssignedJobs;
